import { goRequest } from '../utils/httpClientTs';
import {
  EventGroupResponse,
  UpcomingEventResponse,
  EventDetailResponse,
  RSVPRequest,
  CreateEventRequest,
  CreateAgendaItemRequest,
  UpdateEventRequest,
  UpdateEventDetailsRequest,
  UpdateEventAgendaRequest,
  UpdateEventHostsRequest,
  RecentBusinessLocationResponse,
} from '../types/events';
import { GenericResponse, GenericResponseWithId } from '../types/generic';

// API GET /events/groups?local_chapter_id=... (optional; falls back to user's current chapter)
export const getEventGroups = (
  localChapterId?: string
): Promise<EventGroupResponse[]> => {
  const q =
    localChapterId && localChapterId.trim() !== ''
      ? `?local_chapter_id=${encodeURIComponent(localChapterId.trim())}`
      : '';
  return goRequest<EventGroupResponse[]>({
    url: `/events/groups${q}`,
    method: 'GET',
  });
};

// API GET /events/recent-business-locations?local_chapter_id=...
export const getRecentBusinessLocations = (
  localChapterId: string
): Promise<RecentBusinessLocationResponse[]> => {
  return goRequest<RecentBusinessLocationResponse[]>({
    url: `/events/recent-business-locations?local_chapter_id=${encodeURIComponent(localChapterId.trim())}`,
    method: 'GET',
  });
};

// API GET /events/upcoming?start_date=X&end_date=Y&local_chapter_id=Z&group_id=W&timezone_offset=T
export const getUpcomingEvents = (
  startDate: string,
  endDate: string,
  localChapterId?: string,
  groupId?: string,
  timezoneOffset?: number
): Promise<UpcomingEventResponse[]> => {
  let url = `/events/upcoming?start_date=${startDate}&end_date=${endDate}`;
  if (localChapterId) {
    url += `&local_chapter_id=${localChapterId}`;
  }
  if (groupId) {
    url += `&group_id=${groupId}`;
  }
  if (timezoneOffset !== undefined) {
    url += `&timezone_offset=${timezoneOffset}`;
  }
  return goRequest<UpcomingEventResponse[]>({
    url: url,
    method: 'GET',
  });
};

// API GET /events/:event_id
export const getEventDetail = (
  eventId: string
): Promise<EventDetailResponse> => {
  return goRequest<EventDetailResponse>({
    url: `/events/${eventId}`,
    method: 'GET',
  });
};

// API POST /events/create (returns new event id for navigation / cache updates)
export const createEvent = (
  data: CreateEventRequest
): Promise<GenericResponseWithId> => {
  return goRequest<GenericResponseWithId>({
    url: '/events/create',
    method: 'POST',
    data: data,
  });
};

// API POST /events/:event_id/rsvp
export const rsvpEvent = (
  eventId: string,
  data: RSVPRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}/rsvp`,
    method: 'POST',
    data: data,
  });
};

// API POST /events/:event_id/agenda
export const createEventAgenda = (
  eventId: string,
  data: CreateAgendaItemRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}/agenda`,
    method: 'POST',
    data: data,
  });
};

// API PATCH /events/:event_id
export const updateEvent = (
  eventId: string,
  data: UpdateEventRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}`,
    method: 'PATCH',
    data: data,
  });
};

// API PUT /events/:event_id/details (details + description only)
export const updateEventDetails = (
  eventId: string,
  data: UpdateEventDetailsRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}/details`,
    method: 'PUT',
    data: data,
  });
};

// API PUT /events/:event_id/agenda (replace all agenda items)
export const updateEventAgenda = (
  eventId: string,
  data: UpdateEventAgendaRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}/agenda`,
    method: 'PUT',
    data: data,
  });
};

// API PUT /events/:event_id/hosts (replace all hosts)
export const updateEventHosts = (
  eventId: string,
  data: UpdateEventHostsRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}/hosts`,
    method: 'PUT',
    data: data,
  });
};

// API DELETE /events/:event_id
export const deleteEvent = (eventId: string): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/events/${eventId}`,
    method: 'DELETE',
  });
};
